// Editorial specimen catalogue for the Espresso "Modern Printing House".
// Each use case is a printed work with its own production pipeline.

export const USE_CASES = [
  {
    slug: "books",
    label: "A Book",
    kind: "Book",
    category: "Writing & Publishing",
    ref: "SPEC. 001",
    tagline: "From an idea to a bound manuscript.",
    excerpt: "Multi-chapter hardcover or digital manuscript, complete with front matter, footnotes and a cover index.",
    pipeline: ["Idea", "Questions", "Research", "Master Index", "Chapters", "Writing", "Illustrations", "Editing", "Manuscript", "Publication"],
    example: { title: "The Human Body", meta: "24 chapters · 312 pages", chapters: ["Building Blocks", "Cells & Tissues", "Major Organs", "The Heart", "The Brain", "Systems in Concert"] },
    cta: "Start a Book",
  },
  {
    slug: "articles",
    label: "An Article",
    kind: "Article",
    category: "Writing & Publishing",
    ref: "SPEC. 002",
    tagline: "From an angle to a finished piece.",
    excerpt: "Deep-dive investigative journalism and editorial essays, typeset and ready for syndication.",
    pipeline: ["Idea", "Angle", "Sources", "Draft", "Review", "Typeset", "Publish"],
    example: { title: "The Quiet Revolution in Type", meta: "4,200 words · 3 sources", chapters: ["Lede", "Context", "Evidence", "Counterpoint", "Close"] },
    cta: "Start an Article",
  },
  {
    slug: "courses",
    label: "A Course",
    kind: "Course",
    category: "Education",
    ref: "SPEC. 003",
    tagline: "From a syllabus to a full course kit.",
    excerpt: "Structured learning paths with lecture scripts, workbooks and assessments in reading folios.",
    pipeline: ["Syllabus", "Curriculum", "Lecture Scripts", "Workbooks", "Assessments", "Course Kit"],
    example: { title: "Foundations of Astrophysics", meta: "12 modules · 48 lessons", chapters: ["Orientation", "Mechanics", "Light & Matter", "Stars", "Cosmology", "Capstone"] },
    cta: "Start a Course",
  },
  {
    slug: "screenplays",
    label: "A Film",
    kind: "Screenplay",
    category: "Film & Storytelling",
    ref: "SPEC. 004",
    tagline: "From a logline to a production book.",
    excerpt: "Full cinematographic bibles, scene indexes, character arcs and formatted screenplays.",
    pipeline: ["Concept", "Logline", "Character Bible", "Treatment", "Screenplay", "Storyboard", "Production Book"],
    example: { title: "Northlight", meta: "Feature · 108 pages", chapters: ["Act I", "Inciting Incident", "Midpoint", "Act II", "Climax", "Resolution"] },
    cta: "Start a Film",
  },
  {
    slug: "knowledge-bases",
    label: "A Knowledge Base",
    kind: "Knowledge Base",
    category: "Knowledge",
    ref: "SPEC. 005",
    tagline: "From scattered notes to a living compendium.",
    excerpt: "Enterprise or personal research organised into a searchable, cross-linked master index.",
    pipeline: ["Topics", "Taxonomy", "Extraction", "Cross-Linking", "Master Index", "Live Compendium"],
    example: { title: "Company Handbook", meta: "540 entries · fully indexed", chapters: ["People", "Process", "Product", "Policies", "Glossary"] },
    cta: "Start a Knowledge Base",
  },
  {
    slug: "research",
    label: "A Research Project",
    kind: "Research",
    category: "Education",
    ref: "SPEC. 006",
    tagline: "From a hypothesis to a peer-ready paper.",
    excerpt: "Academic papers, systematic reviews and technical disclosures with citations and figures.",
    pipeline: ["Hypothesis", "Literature Review", "Analysis", "Drafting", "Citations", "Peer Specimen"],
    example: { title: "On Sleep & Memory", meta: "IMRaD · 42 references", chapters: ["Abstract", "Introduction", "Methods", "Results", "Discussion"] },
    cta: "Start Research",
  },
  {
    slug: "engineering",
    label: "An Engineering Document",
    kind: "Engineering",
    category: "Science & Engineering",
    ref: "SPEC. 007",
    tagline: "From specs to a rigorous manual.",
    excerpt: "Technical manuals, blueprints and system documentation under clean revision control.",
    pipeline: ["Specs", "Architecture", "Schematics", "API Spec", "Manual Draft", "Revision Control"],
    example: { title: "Turbine Service Manual", meta: "Rev. C · 180 pages", chapters: ["Overview", "Assembly", "Operation", "Maintenance", "Troubleshooting", "Appendix"] },
    cta: "Start a Manual",
  },
  {
    slug: "business",
    label: "A Business Plan",
    kind: "Business",
    category: "Business",
    ref: "SPEC. 008",
    tagline: "From a strategy to an investor-ready document.",
    excerpt: "Business plans, strategy documents, market research and product documentation.",
    pipeline: ["Thesis", "Market Research", "Model", "Financials", "Narrative", "Deck & Doc"],
    example: { title: "Northstar Ventures", meta: "Seed · 28 pages", chapters: ["Summary", "Market", "Product", "Model", "Team", "Financials"] },
    cta: "Start a Plan",
  },
  {
    slug: "essays",
    label: "An Essay",
    kind: "Essay",
    category: "Writing & Publishing",
    ref: "SPEC. 009",
    tagline: "From a thesis to a persuasive argument.",
    excerpt: "Long-form essays and opinion pieces with a clear argument, evidence and a memorable close.",
    pipeline: ["Thesis", "Outline", "Evidence", "Draft", "Refine", "Typeset"],
    example: { title: "On Attention", meta: "3,000 words", chapters: ["Hook", "Claim", "Evidence", "Objection", "Close"] },
    cta: "Start an Essay",
  },
  {
    slug: "biographies",
    label: "A Biography",
    kind: "Biography",
    category: "Writing & Publishing",
    ref: "SPEC. 010",
    tagline: "From a life to a lasting portrait.",
    excerpt: "Researched life stories with a timeline, sources and richly narrated chapters.",
    pipeline: ["Subject", "Timeline", "Research", "Chapters", "Narration", "Editing", "Manuscript"],
    example: { title: "The Cartographer", meta: "18 chapters · 260 pages", chapters: ["Early Years", "Rise", "The Work", "Legacy", "Sources"] },
    cta: "Start a Biography",
  },
  {
    slug: "encyclopedias",
    label: "An Encyclopedia",
    kind: "Encyclopedia",
    category: "Knowledge",
    ref: "SPEC. 011",
    tagline: "From a subject to an ordered body of entries.",
    excerpt: "Comprehensive, cross-referenced reference works with alphabetised entries and a master index.",
    pipeline: ["Scope", "Entry List", "Research", "Entries", "Cross-References", "Master Index"],
    example: { title: "Encyclopedia of Type", meta: "420 entries", chapters: ["A–D", "E–H", "I–M", "N–R", "S–Z", "Index"] },
    cta: "Start an Encyclopedia",
  },
  {
    slug: "textbooks",
    label: "A Textbook",
    kind: "Textbook",
    category: "Education",
    ref: "SPEC. 012",
    tagline: "From a curriculum to a classroom-ready text.",
    excerpt: "Pedagogical textbooks with chapters, worked examples, exercises and review questions.",
    pipeline: ["Curriculum", "Chapters", "Explanations", "Examples", "Exercises", "Review", "Edition"],
    example: { title: "Introductory Chemistry", meta: "16 chapters · exercises", chapters: ["Matter", "Atoms", "Bonds", "Reactions", "Solutions", "Review"] },
    cta: "Start a Textbook",
  },
  {
    slug: "worldbuilding",
    label: "A World",
    kind: "Worldbuilding",
    category: "Worlds & Ideas",
    ref: "SPEC. 013",
    tagline: "From a spark to an entire universe.",
    excerpt: "Fictional worlds with geography, history, cultures, characters and lore — one connected bible.",
    pipeline: ["Premise", "Geography", "History", "Cultures", "Characters", "Lore", "World Bible"],
    example: { title: "The Aetherlands", meta: "World Bible · 140 pages", chapters: ["Map", "Ages", "Peoples", "Powers", "Legends"] },
    cta: "Start a World",
  },
];

// Per-specimen share art (Open Graph covers) — each /use-cases/:slug link gets its own cover.
const OG_BASE = "https://static.prod-images.emergentagent.com/jobs/2dbac991-9431-4c1c-baca-4d7548e28de1/images/";
const OG_COVERS = {
  books: "d8a999d5436cc37e6628704b045184bbc5ba9d61a52b2cbf9503144bd0cf0195.jpeg",
  articles: "72239d51bd2cfe3bbdf38d713c1ddda1e54125427a630a5ae74900065d5c1cb8.jpeg",
  courses: "58edcbfbb9cde50df81ae5cba72b9eb9024747ffaac3964a8f5f44004134867f.jpeg",
  screenplays: "ea89a7d503ff28e438b0b3a26cc10e0fcd4ab6909a520f87346043ce31c7d0fd.jpeg",
  "knowledge-bases": "b2f26e5596361865c281c2c04ccffb8fcd00ce624398423e60c6a324da174a07.jpeg",
  research: "d2eecb75b7c620c6255facdd65bd78616e313557b8ae903fa6756bdbae39aa8b.jpeg",
  engineering: "4015462f0121c6685d66546d3c9575c5d7c85c65668315c127a7dd21c566ffa1.jpeg",
  business: "c4c2fc4bf43e07bf17e99852d5422500b838e3c3c7855fff53b26c6e263fb1c5.jpeg",
  essays: "80fc772b7c94c8713e1a0097cf0a91960161f834f51f7d94382d1c2cb367af0d.jpeg",
  biographies: "42a84d5b8734aeb97ad11751082445451500a54fde2fa881a2ce8361a87fd934.jpeg",
  encyclopedias: "8911607589e81d4d07f8ef037c0c1c12d82c7693a7172882c5a40bc571cfc8ea.jpeg",
  textbooks: "1a033ec140bc6b3e51d1a4126582197c8d024ea1f5f33ddfe1f09a41c94dd372.jpeg",
  worldbuilding: "8f90f14e4884709f0a7f42e8160212e41539789ddfe7233415e6c8f15542146b.jpeg",
};
USE_CASES.forEach((u) => {
  if (OG_COVERS[u.slug]) u.og = OG_BASE + OG_COVERS[u.slug];
});

// The homepage selector + preview grid stay curated; the full catalogue shows everything.
export const FEATURED_SLUGS = ["books", "articles", "courses", "screenplays", "knowledge-bases", "research", "engineering", "business"];
export const FEATURED = USE_CASES.filter((u) => FEATURED_SLUGS.includes(u.slug));

export const HERO_CHIPS = ["Knowledge Base", "Book", "Research", "Engineering Manual", "Course", "Policy Handbook", "Encyclopedia", "Whitepaper"];
export const PRODUCTION_SEQUENCE = ["INGEST", "DECOMPOSE", "STRUCTURE", "PROCESS", "QUALITY", "SYNTHESISE", "PUBLISH"];

export const CATEGORY_ORDER = [
  "Writing & Publishing",
  "Knowledge",
  "Education",
  "Film & Storytelling",
  "Science & Engineering",
  "Business",
  "Worlds & Ideas",
];

export const getUseCase = (slug) => USE_CASES.find((u) => u.slug === slug);
